// TourGroupItem.tsx
import styled from "styled-components";
import { TourGroup } from "./types";
import { useFetchEntities } from "@/reactQueryHooks";
import { v4 as uuidv4 } from "uuid";
import RoleDraggableItem from "./RoleDraggableItem";
import VehicleDraggableItem from "./VehicleDraggableItem";
import LaunchIcon from "@mui/icons-material/Launch";
import TourGroupModal from "@/components/bookings_manager/tour_guides/content/TourGroupModal";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import AssignedRoleBadge from "./AssignedRoleBadge";
import AssignedVehicleBadge from "./AssignedVehicleBadge";
import { refreshStaffScheduleById } from "../staff_planner/signalStore";
import OmittedRolesDropArea from "./OmittedRolesDropArea";
import Button from "@mui/material/Button";
import {
  useGetUserDaySchedulesThatIncludesTourGroup,
  useGetUserDaySchedulesThatIncludesTourGroupFromSchedule,
  useFetchTourGroupForScheduleById,
} from "@/reactQueryHooks";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment";
import { ScheduleNote } from "./types";
import NewScheduleNote from "./NewScheduleNote";
import ScheduleNoteItem from "./ScheduleNoteItem";
import { useState, useEffect, useContext } from "react";
import { Context } from "../SchedulePlanner";

const TourGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  //border-radius: 3px;
  background-color: #ffffff;
  position: relative;
`;

const CornerRibbon = styled.div<{
  allAssigned: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 40px;
  height: 40px;
  overflow: hidden;
  z-index: auto;
  &:before {
    content: "";
    position: absolute;
    display: block;
    width: 60px;
    height: 12px;
    background-color: ${(props) => (props.allAssigned ? "#40c057" : "#fa5252")};
    transform: rotate(-45deg) translateY(-15px) translateX(-15px);
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
  }
`;

const TourHeaderRow = styled.div`
  display: flex;
  //justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
`;

const TourTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  color: #212529;
`;

const TourLinkIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  &:hover {
    background-color: #e9ecef;
  }
`;

const Date = styled.div`
  font-size: 12px;
  color: #495057;
  background-color: #f1f3f5;
  padding: 4px 8px;
  border-radius: 50px;
  font-weight: 500;
  display: flex;
  align-items: center;
`;

const StartTime = styled.div`
  font-size: 12px;
  color: #495057;
  background-color: #f1f3f5;
  padding: 4px 8px;
  border-radius: 50px;
  font-weight: 500;
  display: flex;
  align-items: center;

  &::before {
    content: "🕒";
    margin-right: 4px;
    font-size: 10px;
  }
`;

const Index = styled.div`
  font-size: 12px;
  color: #495057;
  background-color: #f1f3f5;
  padding: 4px 8px;
  border-radius: 50px;
  font-weight: 500;
  display: flex;
  align-items: center;
`;

const Vehicle = styled.div`
  font-size: 12px;
  color: #495057;
  background-color: #f1f3f5;
  padding: 4px 8px;
  border-radius: 50px;
  font-weight: 500;
  display: flex;
  align-items: center;

  &::before {
    content: "🚌";
    margin-right: 4px;
    font-size: 11px;
  }
`;

const Tickets = styled.div`
  font-size: 12px;
  color: #495057;
  background-color: #f1f3f5;
  padding: 4px 8px;
  border-radius: 50px;
  font-weight: 500;
  display: flex;
  align-items: center;

  &::before {
    content: "👥";
    margin-right: 4px;
    font-size: 10px;
  }
`;

const TourOption = styled.div`
  font-size: 12px;
  color: #2a6294;
  display: flex;
  align-items: center;

  &::before {
    content: "🏷️";
    margin-right: 6px;
    font-size: 12px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e9ecef;
`;

const RolesContainer = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

// New styled components for the assignees organization
const AssigneesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const RoleSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const SectionHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #185592;
  //margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RoleAssigneesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  //padding-left: 8px;
`;

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
`;

const TourGroupItem = ({ tourGroupId }: { tourGroupId: string }) => {
  const { setShowTourGroupModal, setTourGroupModalId } = useContext(Context);
  const { data: entities } = useFetchEntities() as any;
  const [showAddNote, setShowAddNote] = useState(false);

  const {
    data: tourGroup,
    isFetching,
    refetch,
  } = useFetchTourGroupForScheduleById(tourGroupId);

  const { data: assigneesUsds, refetch: refetchAssignees } =
    useGetUserDaySchedulesThatIncludesTourGroupFromSchedule(tourGroupId || "");

  useEffect(() => {
    setShowAddNote(false);
  }, [isFetching]);

  if (isFetching)
    return (
      <Skeleton
        height={300}
        baseColor={"transparent"}
        borderRadius={7}
        duration={0.7}
        highlightColor={"white"}
      />
    );

  if (!tourGroup) return <div>No tour group found</div>;

  const {
    product_id,
    option_id,
    start_time_id,
    totalTickets,
    vehicle_id,
    omitted_roles,
  } = tourGroup;

  const vehicle = entities.data.vehicles.find(
    (vehicle: any) => vehicle._id === vehicle_id
  );

  const product = entities.data.products.find(
    (product: any) => product._id === product_id
  );
  const roles = entities.data.roles;
  const option = product?.options.find((o: any) => o._id === option_id);
  const optionRequiresVehicle = option?.requires_vehicle;

  const optionRolesIds = option?.crewRoles.filter((roleId: string) => {
    const role = roles.find((r: any) => r._id === roleId);
    return role && role.shouldReceiveSchedule;
  });

  if (tourGroupId === "67449b73e47e4cb4ae895fd7") {
    const optionRolesIdsWithoutOmitted = optionRolesIds?.filter(
      (roleId: string) => !omitted_roles?.includes(roleId)
    );
    console.log("optionRolesIds", optionRolesIds);
    console.log("optionRolesIdsWithoutOmitted", optionRolesIdsWithoutOmitted);
  }

  const startTime = product?.start_times.find(
    (st: any) => st._id === start_time_id
  );

  const assignedRoles = assigneesUsds
    ?.map((usd: any) =>
      usd?.tourGroups
        ?.filter((tg: any) => tg?.id === tourGroupId) //filter tour groups by the current tour group id
        .map((tg: any) => tg?.role)
    )
    .flat(1);

  const optionRolesIdsWithoutOmitted = optionRolesIds?.filter(
    (roleId: string) => !omitted_roles?.includes(roleId)
  );
  const allRolesAssigned = optionRolesIdsWithoutOmitted?.every(
    (roleId: string) => assignedRoles?.includes(roleId)
  );

  // Group assignees by role
  const assigneesByRole: Record<string, any[]> = {};

  if (assigneesUsds?.length > 0) {
    assigneesUsds.forEach((assigneeUsd: any) => {
      const tourGroupInUsd = assigneeUsd?.tourGroups.find(
        (tg: any) => tg?.id === tourGroupId
      );

      if (tourGroupInUsd?.role) {
        if (!assigneesByRole[tourGroupInUsd.role]) {
          assigneesByRole[tourGroupInUsd.role] = [];
        }
        assigneesByRole[tourGroupInUsd.role].push(assigneeUsd);
      }
    });
  }

  const scheduleNotes = tourGroup.schedule_notes_list?.length
    ? tourGroup.schedule_notes_list
    : [];

  const hasScheduleNotes = scheduleNotes.length > 0;

  return (
    <TourGroupContainer>
      <Tooltip
        title={
          allRolesAssigned ? "All roles assigned" : "Not all roles assigned"
        }
      >
        <CornerRibbon allAssigned={allRolesAssigned} />
      </Tooltip>

      <TourHeaderRow
        style={{
          paddingLeft: "10px",
        }}
      >
        <TourTitle>{product?.title}</TourTitle>
        <TourLinkIconContainer
          onClick={() => {
            setTourGroupModalId(tourGroupId);
            setShowTourGroupModal(true);
          }}
        >
          <LaunchIcon
            sx={{
              fontSize: "16px",
            }}
          />
        </TourLinkIconContainer>
      </TourHeaderRow>

      <TourHeaderRow>
        <Date>{moment(tourGroup?.date).format("DD MMM YYYY")}</Date>
        <StartTime>{startTime?.time_slot}</StartTime>
        <Index>Group {tourGroup?.index}</Index>
        {/* <Vehicle>{vehicle?.plate || "N/A"}</Vehicle> */}
        <Tickets>{totalTickets}</Tickets>
      </TourHeaderRow>

      <TourOption>{option?.title}</TourOption>

      <Divider />

      <SectionHeader>Available Roles</SectionHeader>
      <RolesContainer>
        {optionRolesIdsWithoutOmitted?.map((roleId: string) => {
          const isAssigned = assignedRoles?.includes(roleId);

          return (
            <RoleDraggableItem
              key={uuidv4()}
              date={tourGroup.date}
              roleId={roleId}
              tourGroupId={tourGroup._id}
              isAssigned={isAssigned}
            />
          );
        })}
      </RolesContainer>

      {!vehicle && optionRequiresVehicle && (
        <>
          <Divider />
          <div style={{ display: "flex", alignItems: "center" }}>
            <VehicleDraggableItem
              date={tourGroup.date}
              tourGroupId={tourGroup._id}
              isAssigned={vehicle_id ? true : false}
            />
          </div>
        </>
      )}

      {Object.keys(assigneesByRole).length > 0 && (
        <>
          <Divider />
          <AssigneesContainer>
            {optionRolesIds?.map((roleId: string) => {
              const roleAssignees = assigneesByRole[roleId] || [];
              const role = roles.find((r: any) => r._id === roleId);

              // Only show role sections that have assignees
              if (roleAssignees.length === 0) return null;

              return (
                <RoleSectionContainer key={roleId}>
                  <SectionHeader>{role?.title}</SectionHeader>
                  <RoleAssigneesContainer>
                    {roleAssignees.map((assigneeUsd: any) => (
                      <AssignedRoleBadge
                        key={uuidv4()}
                        tourGroupId={tourGroup._id}
                        assigneeUsd={assigneeUsd}
                        refetch={refetchAssignees}
                      />
                    ))}
                  </RoleAssigneesContainer>
                </RoleSectionContainer>
              );
            })}
          </AssigneesContainer>
        </>
      )}

      {vehicle_id && (
        <>
          <Divider />
          <SectionHeader>Vehicle</SectionHeader>
          <RoleAssigneesContainer>
            <AssignedVehicleBadge
              date={tourGroup.date}
              tourGroupId={tourGroup._id}
              vehicleId={vehicle_id}
              //isProjected={tourGroup?.is_projected_vehicle}
              refetch={refetch}
            />
          </RoleAssigneesContainer>
        </>
      )}

      {/* Schedule Notes Section */}
      <Divider />

      <>
        <SectionHeader>Omitted Roles</SectionHeader>
        <OmittedRolesDropArea
          omittedRoles={omitted_roles || []}
          tourGroupId={tourGroup._id}
          refetch={refetch}
        />
      </>

      <Divider />

      <NotesContainer>
        <Button size="small" onClick={() => setShowAddNote(!showAddNote)}>
          {showAddNote ? "Cancel" : "Add Note"}
        </Button>

        {showAddNote && (
          <NewScheduleNote refetch={refetch} tourGroupId={tourGroup._id} />
        )}

        {hasScheduleNotes &&
          scheduleNotes.map((note: ScheduleNote) => (
            <ScheduleNoteItem
              key={note?._id || uuidv4()}
              tourGroupId={tourGroup._id}
              note={note}
              refetch={refetch}
            />
          ))}
      </NotesContainer>
    </TourGroupContainer>
  );
};

export default TourGroupItem;
